import React, { useState } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import type { FeedbackAnalyticsDto } from '../../../../services/feedback-analytics';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Type declaration for jsPDF autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: any;
  }
}

interface ExportButtonsProps {
  data: FeedbackAnalyticsDto | null;
  loading?: boolean;
}

export const ExportButtons: React.FC<ExportButtonsProps> = ({
  data,
  loading = false,
}) => {
  const [exporting, setExporting] = useState(false);

  const getReportInsights = (analytics: FeedbackAnalyticsDto) => {
    const totalRatings = analytics.ratingDistribution.reduce(
      (sum, item) => sum + item.count,
      0,
    );

    const totalAnonymous = analytics.allForms.reduce(
      (sum, form) => sum + form.anonymousCount,
      0,
    );
    const totalNamed = analytics.allForms.reduce(
      (sum, form) => sum + form.namedCount,
      0,
    );
    const identifiedBase = totalAnonymous + totalNamed;

    const unlabeledQuestions = analytics.allForms.reduce((sum, form) => {
      const unlabeledByForm = form.questions.filter(
        (question) => question.label === 'Pergunta sem rótulo',
      ).length;
      return sum + unlabeledByForm;
    }, 0);

    const topByNps = [...analytics.allForms]
      .sort((a, b) => (b.npsSummary?.npsScore || 0) - (a.npsSummary?.npsScore || 0))
      .slice(0, 3);

    const topByAverage = [...analytics.allForms]
      .sort((a, b) => b.averageRating - a.averageRating)
      .slice(0, 3);

    return {
      totalRatings,
      totalAnonymous,
      totalNamed,
      anonymousPercentage:
        identifiedBase > 0 ? Math.round((totalAnonymous / identifiedBase) * 100) : 0,
      namedPercentage:
        identifiedBase > 0 ? Math.round((totalNamed / identifiedBase) * 100) : 0,
      unlabeledQuestions,
      topByNps,
      topByAverage,
    };
  };

  const handleExportPDF = async () => {
    if (!data) {
      alert('Nenhum dado para exportar');
      return;
    }

    try {
      setExporting(true);
      const insights = getReportInsights(data);
      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 10;

      // Título
      doc.setFontSize(16);
      doc.text('Relatório de Análise de Feedback - Hotel', 10, yPosition);
      yPosition += 10;

      // Data de geração
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 10, yPosition);
      yPosition += 10;

      // Resumo
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Resumo Executivo', 10, yPosition);
      yPosition += 7;

      doc.setFontSize(10);
      const summaryTable = [
        ['Métrica', 'Valor'],
        ['Total de Reviews', data.totalReviews.toString()],
        ['Avaliação Média', `${data.averageRating.toFixed(1)}/10`],
        ['NPS', data.npsSummary.npsScore.toString()],
        ['Promotores', `${data.npsSummary.promotersCount} (${data.npsSummary.promotersPercentage}%)`],
        ['Neutros', `${data.npsSummary.passivesCount} (${data.npsSummary.passivesPercentage}%)`],
        ['Detratores', `${data.npsSummary.detractorsCount} (${data.npsSummary.detractorsPercentage}%)`],
        ['Período', `${new Date(data.dateRange.from).toLocaleDateString('pt-BR')} a ${new Date(data.dateRange.to).toLocaleDateString('pt-BR')}`],
      ];

      (doc as any).autoTable({
        head: [summaryTable[0]],
        body: summaryTable.slice(1),
        startY: yPosition,
        margin: 10,
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 240, 240] },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;

      // Qualidade da base
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = 10;
      }

      doc.setFontSize(12);
      doc.text('Qualidade da Base', 10, yPosition);
      yPosition += 7;

      const qualityTable = [
        ['Indicador', 'Valor'],
        ['Respostas com nota válida', insights.totalRatings.toString()],
        [
          'Anonimato (base identificável)',
          `${insights.totalAnonymous} (${insights.anonymousPercentage}%)`,
        ],
        [
          'Identificadas (base identificável)',
          `${insights.totalNamed} (${insights.namedPercentage}%)`,
        ],
        ['Perguntas sem rótulo', insights.unlabeledQuestions.toString()],
      ];

      (doc as any).autoTable({
        head: [qualityTable[0]],
        body: qualityTable.slice(1),
        startY: yPosition,
        margin: 10,
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 240, 240] },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;

      // Distribuição de Notas
      if (yPosition > pageHeight - 40) {
        doc.addPage();
        yPosition = 10;
      }

      doc.setFontSize(12);
      doc.text('Distribuição de Notas', 10, yPosition);
      yPosition += 7;

      const ratingTable = [
        ['Nota', 'Quantidade', 'Percentual'],
        ...data.ratingDistribution
            .filter((r: any) => r.count > 0)
            .map((r: any) => [
              r.score.toString(),
              r.count.toString(),
              `${r.percentage}%`,
            ]),
      ];

      (doc as any).autoTable({
        head: [ratingTable[0]],
        body: ratingTable.slice(1),
        startY: yPosition,
        margin: 10,
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 240, 240] },
      });

      yPosition = (doc as any).lastAutoTable.finalY + 10;

      // Formulários
      if (data.allForms.length > 0) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 10;
        }

        doc.setFontSize(12);
        doc.text('Análise por Formulário', 10, yPosition);
        yPosition += 7;

        const formsTable = [
          ['Formulário', 'Avaliações', 'Média', 'NPS', 'Anônimas'],
          ...data.allForms.map((form: any) => [
            form.formTitle,
            form.totalReviews.toString(),
            form.averageRating.toFixed(1),
            String(form?.npsSummary?.npsScore ?? 0),
            form.anonymousCount.toString(),
          ]),
        ];

        (doc as any).autoTable({
          head: [formsTable[0]],
          body: formsTable.slice(1),
          startY: yPosition,
          margin: 10,
          theme: 'grid',
          headStyles: { fillColor: [13, 110, 253], textColor: 255 },
          alternateRowStyles: { fillColor: [240, 240, 240] },
        });

        yPosition = (doc as any).lastAutoTable.finalY + 10;

        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 10;
        }

        doc.setFontSize(12);
        doc.text('Top 3 Formulários por NPS', 10, yPosition);
        yPosition += 7;

        const topNpsTable = [
          ['Formulário', 'NPS', 'Média', 'Avaliações'],
          ...insights.topByNps.map((form) => [
            form.formTitle,
            String(form?.npsSummary?.npsScore ?? 0),
            form.averageRating.toFixed(1),
            String(form.totalReviews),
          ]),
        ];

        (doc as any).autoTable({
          head: [topNpsTable[0]],
          body: topNpsTable.slice(1),
          startY: yPosition,
          margin: 10,
          theme: 'grid',
          headStyles: { fillColor: [25, 135, 84], textColor: 255 },
          alternateRowStyles: { fillColor: [240, 240, 240] },
        });
      }

      yPosition = (doc as any).lastAutoTable.finalY + 10;
      if (yPosition > pageHeight - 20) {
        doc.addPage();
        yPosition = 10;
      }

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(
        'Nota: Este relatório usa apenas os dados atualmente disponíveis no dashboard de feedback analytics.',
        10,
        yPosition,
      );
      yPosition += 5;
      doc.text(
        'Dados operacionais de tratativa (aberto/em andamento/resolvido/SLA) não estão disponíveis nesta versão.',
        10,
        yPosition,
      );

      // Salvar PDF
      doc.save(
        `relatorio-feedback-${new Date().toISOString().split('T')[0]}.pdf`,
      );
      alert('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => {
    if (!data) {
      alert('Nenhum dado para exportar');
      return;
    }

    try {
      const insights = getReportInsights(data);
      let csv = 'RELATÓRIO DE ANÁLISE DE FEEDBACK - HOTEL\n';
      csv += `Gerado em: ${new Date().toLocaleDateString('pt-BR')}\n\n`;

      // Resumo
      csv += 'RESUMO EXECUTIVO\n';
      csv += 'Métrica,Valor\n';
      csv += `Total de Reviews,${data.totalReviews}\n`;
      csv += `Avaliação Média,${data.averageRating.toFixed(1)}/10\n`;
      csv += `NPS,${data.npsSummary.npsScore}\n`;
      csv += `Promotores,${data.npsSummary.promotersCount} (${data.npsSummary.promotersPercentage}%)\n`;
      csv += `Neutros,${data.npsSummary.passivesCount} (${data.npsSummary.passivesPercentage}%)\n`;
      csv += `Detratores,${data.npsSummary.detractorsCount} (${data.npsSummary.detractorsPercentage}%)\n`;
      csv += `Período,"${new Date(data.dateRange.from).toLocaleDateString('pt-BR')} a ${new Date(data.dateRange.to).toLocaleDateString('pt-BR')}"\n\n`;

      // Qualidade da base
      csv += 'QUALIDADE DA BASE\n';
      csv += 'Indicador,Valor\n';
      csv += `Respostas com nota válida,${insights.totalRatings}\n`;
      csv += `Anonimato (base identificável),${insights.totalAnonymous} (${insights.anonymousPercentage}%)\n`;
      csv += `Identificadas (base identificável),${insights.totalNamed} (${insights.namedPercentage}%)\n`;
      csv += `Perguntas sem rótulo,${insights.unlabeledQuestions}\n\n`;

      // Distribuição de Notas
      csv += 'DISTRIBUIÇÃO DE NOTAS\n';
      csv += 'Nota,Quantidade,Percentual\n';
      data.ratingDistribution
        .filter((r: any) => r.count > 0)
        .forEach((r: any) => {
          csv += `${r.score},${r.count},${r.percentage}%\n`;
        });
      csv += '\n';

      // Formulários
      csv += 'ANÁLISE POR FORMULÁRIO\n';
      csv += 'Formulário,Avaliações,Média,NPS,Anônimas,Identificadas\n';
      data.allForms.forEach((form: any) => {
        csv += `"${form.formTitle}",${form.totalReviews},${form.averageRating.toFixed(1)},${form?.npsSummary?.npsScore ?? 0},${form.anonymousCount},${form.namedCount}\n`;
      });
      csv += '\n';

      // Ranking por NPS
      csv += 'TOP 3 FORMULÁRIOS POR NPS\n';
      csv += 'Formulário,NPS,Média,Avaliações\n';
      insights.topByNps.forEach((form) => {
        csv += `"${form.formTitle}",${form?.npsSummary?.npsScore ?? 0},${form.averageRating.toFixed(1)},${form.totalReviews}\n`;
      });
      csv += '\n';

      // Ranking por média
      csv += 'TOP 3 FORMULÁRIOS POR MÉDIA\n';
      csv += 'Formulário,Média,NPS,Avaliações\n';
      insights.topByAverage.forEach((form) => {
        csv += `"${form.formTitle}",${form.averageRating.toFixed(1)},${form?.npsSummary?.npsScore ?? 0},${form.totalReviews}\n`;
      });
      csv += '\n';

      csv += 'OBSERVAÇÕES\n';
      csv += 'Este relatório usa apenas os dados atualmente disponíveis no dashboard de feedback analytics.\n';
      csv += 'Dados operacionais de tratativa (aberto/em andamento/resolvido/SLA) não estão disponíveis nesta versão.\n';

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `relatorio-feedback-${new Date().toISOString().split('T')[0]}.csv`,
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert('CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      alert('Erro ao exportar CSV');
    }
  };

  return (
    <div className="mb-4">
      <ButtonGroup>
        <Button
          variant="primary"
          onClick={handleExportPDF}
          disabled={loading || exporting}
        >
          📄 Exportar PDF
        </Button>
        <Button
          variant="secondary"
          onClick={handleExportCSV}
          disabled={loading || exporting}
        >
          📊 Exportar CSV
        </Button>
      </ButtonGroup>
    </div>
  );
};

export default ExportButtons;
